<template>
  <v-container fluid class="pa-4">
    <PageHeader
      icon="mdi-map-marker-radius"
      title="Pengurusan Negeri & Daerah"
      subtitle="Selenggara jadual rujukan negeri dan daerah yang digunakan oleh Jadual Awalan, Lampiran dan Wajaran"
    />

    <!-- Nota sumber data. Diletak di atas tab, bukan dalam salah satu tab,
         kerana ia terpakai pada kedua-duanya: `lookup_daerah` ialah jadual
         legasi dan TIDAK boleh disunting di sini atau di mana-mana. -->
    <v-alert type="info" text dense border="left" class="mb-4 nota-sumber">
      <div class="font-weight-medium">Nota sumber data</div>
      <div class="caption">
        Modul ini menyunting <strong>dua jadual sahaja</strong>:
        <code>lookupnegeri</code> dan <code>lampiran_daerah</code>.
        Jadual legasi <code>lookup_daerah</code> <strong>tidak digunakan</strong> dan tidak
        disunting di sini &mdash; <code>lampiran_daerah</code> ialah sumber kebenaran
        rasmi bagi pemetaan negeri &times; daerah.
      </div>
    </v-alert>

    <v-card outlined>
      <v-card-title class="py-3 kad__tajuk d-flex align-center">
        <v-icon small class="mr-2">mdi-map-marker-radius</v-icon>
        <span class="subtitle-1 font-weight-medium">{{ activeCfg ? activeCfg.label : '' }}</span>
        <span v-if="activeCfg" class="ml-2 text-caption grey--text">({{ activeCfg.table }})</span>
        <v-spacer></v-spacer>
        <v-btn class="btn-teal text-none" :disabled="!activeCfg" @click="openCreate">
          <v-icon left small>mdi-plus</v-icon> Tambah {{ activeCfg ? activeCfg.label : '' }}
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" color="#037769" slider-color="#037769">
        <v-tab v-for="t in tables" :key="t.key" class="text-none">{{ t.label }}</v-tab>
      </v-tabs>

      <v-card-text class="pt-4">
        <div class="filter-bar mb-4">
          <v-row dense align="end">
            <v-col cols="12" sm="5" md="4">
              <div class="fld__label">Carian</div>
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Cari kod atau nama" persistent-placeholder
                dense outlined clearable hide-details
              ></v-text-field>
            </v-col>
            <!-- Tapisan negeri hanya bermakna untuk senarai daerah. -->
            <v-col v-if="isDaerah" cols="12" sm="4" md="3">
              <div class="fld__label">Tapis Negeri</div>
              <v-select
                v-model="filterNG"
                :items="negeriOptions"
                item-text="text" item-value="value"
                prepend-inner-icon="mdi-filter-variant"
                placeholder="Semua" persistent-placeholder
                dense outlined clearable hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="3" md="2" class="d-flex align-end">
              <span class="text-caption grey--text">{{ filteredRows.length }} baris</span>
            </v-col>
          </v-row>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :loading="loading"
          :items-per-page="50"
          :footer-props="{ 'items-per-page-options': [50, 100, 500, -1] }"
          class="elevation-0 ngd-table"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="item.status === 'Enable' ? 'green lighten-4' : 'grey lighten-2'">
              {{ item.status }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon small class="mr-2" color="#037769" @click="openEdit(item)">mdi-pencil</v-icon>
            <v-icon small color="#d9534f" @click="confirmDelete(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog Tambah / Kemaskini. Medan dijana daripada deskriptor lajur yang
         dihantar oleh GET /negeriDaerah/meta, jadi menambah lajur di backend
         tidak memerlukan perubahan di sini. -->
    <v-dialog v-model="dialog" max-width="560px" persistent>
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">{{ isNew ? 'mdi-plus' : 'mdi-pencil' }}</v-icon>
          <span class="subtitle-1 font-weight-medium">
            {{ isNew ? 'Tambah' : 'Kemaskini' }} {{ activeCfg ? activeCfg.label : '' }}
          </span>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col v-for="col in formColumns" :key="col.name" cols="12">
              <v-select
                v-if="col.type === 'enum'"
                v-model="editedItem[col.name]"
                :items="col.options"
                :label="col.label"
                dense outlined hide-details="auto"
              ></v-select>
              <v-text-field
                v-else
                v-model="editedItem[col.name]"
                :label="col.label"
                :maxlength="col.maxLength"
                :counter="col.maxLength && col.maxLength <= 15 ? col.maxLength : false"
                :hint="fieldHint(col)"
                :persistent-hint="!!fieldHint(col)"
                :error="isShortCode(col)"
                :class="{ 'hint--amaran': isUnknownNG(col) && !isShortCode(col) }"
                dense outlined hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-alert v-if="errorMsg" type="error" dense text class="mt-4 mb-0 caption">{{ errorMsg }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="closeDialog">Batal</v-btn>
          <v-btn class="btn-teal text-none" :loading="saving" :disabled="!!formError" @click="save">Simpan</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="520px">
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-delete-alert</v-icon>
          <span class="subtitle-1 font-weight-medium">Adakah anda pasti untuk memadam?</span>
        </v-card-title>
        <v-card-text class="pt-4 caption">
          Baris ini akan dipadam terus daripada <code>{{ activeCfg ? activeCfg.table : '' }}</code>:
          <div class="mt-2 font-weight-medium">{{ deleteLabel }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="deleteDialog = false">Batal</v-btn>
          <v-btn color="red darken-1" text :loading="deleting" @click="doDelete">Padam</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="4000" :color="snackbarColor">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from '@/plugins/axios'

export default {
  name: 'NegeriDaerahManager',
  data: () => ({
    tables: [],        // deskriptor daripada /negeriDaerah/meta
    tab: 0,
    rows: [],
    negeriRows: [],    // sentiasa dimuat: dipakai untuk tapisan + nama negeri
    search: '',
    filterNG: null,
    loading: false,
    dialog: false,
    isNew: false,
    editedItem: {},
    saving: false,
    errorMsg: '',
    deleteDialog: false,
    deleting: false,
    itemToDelete: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),
  computed: {
    apiUrl () { return process.env.VUE_APP_API_URL },
    activeCfg () { return this.tables[this.tab] || null },
    isDaerah () { return !!this.activeCfg && this.activeCfg.table === 'lampiran_daerah' },
    headers () {
      if (!this.activeCfg) return []
      const cols = this.activeCfg.columns
        // `id` auto-increment tiada makna untuk pengguna — disembunyikan.
        .filter(c => !c.auto)
        .map(c => ({ text: c.label, value: c.name }))
      // Nama negeri dihiasi oleh backend untuk baris daerah.
      if (this.isDaerah) cols.splice(1, 0, { text: 'Negeri', value: 'negeri' })
      return [...cols, { text: 'Tindakan', value: 'actions', sortable: false, width: 100 }]
    },
    // Semasa kemaskini, PK dikunci: menukar kunci = padam + cipta semula.
    formColumns () {
      if (!this.activeCfg) return []
      return this.activeCfg.columns.filter(c => !c.auto && (this.isNew || !c.pk))
    },
    negeriOptions () {
      return this.negeriRows.map(n => ({ text: `${n.negeriID} - ${n.negeri}`, value: n.negeriID }))
    },
    // Ditapis di klien: kedua-dua jadual kecil (<200 baris), jadi satu muat
    // turun penuh lebih pantas daripada satu panggilan setiap ketukan kekunci.
    filteredRows () {
      let out = this.rows
      if (this.isDaerah && this.filterNG) out = out.filter(r => r.NG === this.filterNG)
      const q = this.search.trim().toLowerCase()
      if (!q) return out
      const cols = this.activeCfg ? this.activeCfg.columns.map(c => c.name) : []
      return out.filter(r => cols.some(c => String(r[c] == null ? '' : r[c]).toLowerCase().includes(q)))
    },
    // Cermin peraturan `exactLength` di backend supaya Simpan tidak boleh
    // ditekan hanya untuk menerima 400 sesaat kemudian.
    formError () {
      for (const col of this.formColumns) {
        const v = String(this.editedItem[col.name] == null ? '' : this.editedItem[col.name]).trim()
        if (col.exactLength && v && v.length !== col.exactLength) {
          return `"${col.label}" mesti tepat ${col.exactLength} digit`
        }
      }
      return ''
    },
    deleteLabel () {
      const it = this.itemToDelete
      if (!it) return ''
      return this.isDaerah ? `${it.NG} / ${it.DP} — ${it.NamaDP}` : `${it.negeriID} — ${it.negeri}`
    },
  },
  watch: {
    tab () {
      this.search = ''
      this.filterNG = null
      this.fetchRows()
    },
  },
  async created () {
    await this.fetchMeta()
    await this.fetchRows()
  },
  methods: {
    showSnack (msg, color = 'success') {
      this.snackbarText = msg
      this.snackbarColor = color
      this.snackbar = true
    },
    errText (err, fallback) {
      return (err.response && err.response.data && err.response.data.message) || err.message || fallback
    },
    // Cari negeri bagi satu kod NG. Padanan tepat sahaja: kod disimpan sebagai
    // char(2) berisi sifar di depan ('01'), dan '1' bukan kod yang sah.
    lookupNegeri (raw) {
      const v = String(raw == null ? '' : raw).trim()
      if (!v) return null
      return this.negeriRows.find(n => n.negeriID === v) || null
    },
    isShortCode (col) {
      const v = String(this.editedItem[col.name] == null ? '' : this.editedItem[col.name]).trim()
      return !!col.exactLength && !!v && v.length !== col.exactLength
    },
    isNGField (col) {
      return this.isDaerah && col.name === 'NG'
    },
    // Kod ditaip tetapi tiada padanan (termasuk yang terlalu pendek) — medan
    // diwarnakan amaran dan Simpan dimatikan oleh `formError`.
    isUnknownNG (col) {
      if (!this.isNGField(col)) return false
      const v = String(this.editedItem[col.name] || '').trim()
      return !!v && !this.lookupNegeri(v)
    },
    fieldHint (col) {
      // Sahkan kod NG secara langsung semasa menaip: tanpa ini pengguna hanya
      // tahu negeri mana yang dipilih selepas baris disimpan dan disenaraikan.
      if (this.isNGField(col)) {
        const v = String(this.editedItem[col.name] || '').trim()
        if (!v) return 'Masukkan kod negeri 2 digit (cth. 01)'
        if (col.exactLength && v.length !== col.exactLength) {
          return `Kod mesti tepat ${col.exactLength} digit — tulis 0${v} bukan ${v}`
        }
        const neg = this.lookupNegeri(v)
        return neg ? `Negeri: ${neg.negeri}` : 'Kod negeri tidak dijumpai dalam lookupnegeri'
      }
      if (col.exactLength) return `Tepat ${col.exactLength} digit, dengan sifar di depan (cth. 01)`
      if (col.pk && this.isNew) return 'Kunci utama — tidak boleh diubah selepas disimpan'
      return ''
    },
    async fetchMeta () {
      try {
        const res = await axios.get(`${this.apiUrl}/negeriDaerah/meta`)
        this.tables = res.data.tables || []
      } catch (err) {
        console.error(err)
        this.showSnack(this.errText(err, 'Ralat memuatkan maklumat jadual'), 'error')
      }
    },
    async fetchRows () {
      if (!this.activeCfg) return
      this.loading = true
      try {
        const res = await axios.get(`${this.apiUrl}/negeriDaerah/${this.activeCfg.key}`)
        this.rows = res.data.rows || []
        if (!this.isDaerah) this.negeriRows = this.rows
        else if (!this.negeriRows.length) await this.fetchNegeriForFilter()
      } catch (err) {
        console.error(err)
        this.showSnack(this.errText(err, 'Ralat memuat turun data'), 'error')
      } finally {
        this.loading = false
      }
    },
    // Dipanggil hanya apabila tab Daerah dibuka dahulu dan senarai negeri
    // belum pernah dimuat — ia mengisi dropdown "Tapis Negeri".
    async fetchNegeriForFilter () {
      try {
        const res = await axios.get(`${this.apiUrl}/negeriDaerah/negeri`)
        this.negeriRows = res.data.rows || []
      } catch (err) {
        console.error(err)
      }
    },
    blankItem () {
      const o = {}
      for (const col of this.activeCfg.columns) {
        if (col.auto) continue
        o[col.name] = col.type === 'enum' ? col.options[0] : ''
      }
      return o
    },
    openCreate () {
      this.errorMsg = ''
      this.isNew = true
      this.editedItem = this.blankItem()
      this.dialog = true
    },
    openEdit (item) {
      this.errorMsg = ''
      this.isNew = false
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
    closeDialog () {
      this.dialog = false
      this.errorMsg = ''
    },
    async save () {
      this.errorMsg = ''
      this.saving = true
      const cfg = this.activeCfg
      try {
        const body = {}
        for (const col of this.formColumns) body[col.name] = this.editedItem[col.name]

        if (this.isNew) {
          await axios.post(`${this.apiUrl}/negeriDaerah/${cfg.key}`, body)
        } else {
          await axios.put(`${this.apiUrl}/negeriDaerah/${cfg.key}/${this.editedItem[cfg.pk]}`, body)
        }
        this.closeDialog()
        await this.fetchRows()
        this.showSnack(this.isNew ? `${cfg.label} berjaya ditambah` : `${cfg.label} berjaya dikemaskini`)
      } catch (err) {
        console.error(err)
        this.errorMsg = this.errText(err, 'Ralat menyimpan data')
      } finally {
        this.saving = false
      }
    },
    confirmDelete (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    async doDelete () {
      this.deleting = true
      const cfg = this.activeCfg
      try {
        await axios.delete(`${this.apiUrl}/negeriDaerah/${cfg.key}/${this.itemToDelete[cfg.pk]}`)
        this.deleteDialog = false
        await this.fetchRows()
        this.showSnack(`${cfg.label} berjaya dipadam`)
      } catch (err) {
        console.error(err)
        // Padam boleh ditolak (cth. negeri masih dirujuk oleh lampiran_daerah) —
        // dialog dibiarkan terbuka supaya sebabnya dibaca dahulu.
        this.showSnack(this.errText(err, 'Ralat memadam data'), 'error')
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
/* Kepala kad — jalur putih dengan ikon dan tajuk, sama seperti Konfigurasi
   dan Pengurusan Lampiran. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon {
  color: #037769;
}

/* Nota sumber data: biru maklumat, bukan teal, supaya ia dibaca sebagai
   amaran konteks dan bukan sebahagian daripada kawalan borang. */
.nota-sumber code {
  background: rgba(0, 0, 0, .06);
  box-shadow: none;
  color: inherit;
  font-size: var(--fs-3xs);
  padding: 1px 5px;
}

/* ---------- Bar penapis ---------- */
.filter-bar {
  background: #f7fcfb;
  border: 1px solid #dbeeeb;
  border-radius: 4px;
  padding: 10px 12px 12px;
}
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.filter-bar ::v-deep .v-input fieldset { border-color: #cfeae7; }
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-input__prepend-inner .v-icon,
.filter-bar ::v-deep .v-input__append-inner .v-icon { color: #7aa8a2; }

/* Butang teal — menggantikan `color="primary"` (biru Vuetify). Warna literal
   kerana --teal-* ditakrif pada `.Menu`, bukan pada :root. */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}

/* Petua NG yang tidak dijumpai — ambar (amaran), bukan merah: ia bukan ralat
   pengesahan, baris masih boleh disimpan. */
.hint--amaran ::v-deep .v-messages__message { color: #b26a00; font-weight: 500; }

.ngd-table ::v-deep thead th { background: #e6f6f5; }
.ngd-table ::v-deep tbody td { padding-top: 8px; padding-bottom: 8px; }
</style>
